/**
 * Insights articles. Each entry becomes a route, a sitemap URL with a real
 * lastmod, an RSS item and an Article/BlogPosting schema block.
 *
 * `body` is a small block format rather than raw HTML so the renderer stays
 * in control of typography and so the RSS/noscript generators can produce
 * plain text from the same source.
 *   { h2 } | { p } | { ul: [] } | { quote } | { h3 }
 *
 * CommonJS: read by webpack and by scripts/generate-seo.js.
 */
module.exports = [
  {
    slug: 'what-belongs-in-an-mvp',
    title: 'What belongs in an MVP, and what founders keep putting in that shouldn’t',
    metaTitle: 'What Belongs in an MVP | Codelithic Labs',
    metaDescription:
      'How to scope a first version that answers the question your business depends on, what to leave out, and the signs you have built too much.',
    excerpt:
      'The point of a first version is to answer one question. Most of what founders want to build in round one has nothing to do with that question.',
    category: 'Software Development',
    published: '2026-09-03',
    updated: '2026-09-03',
    readingMinutes: 6,
    tags: ['startups', 'mvp', 'product', 'software development'],
    related: ['build-vs-buy', 'what-a-website-costs-in-calgary'],
    body: [
      { p: 'Every founder we meet has a version of the same list. Accounts, profiles, notifications, an admin dashboard, a settings page, billing, and somewhere near the bottom, the thing the business is actually about.' },
      { p: 'That list isn’t wrong. It’s just the wrong order, and building it in that order is how six months disappear.' },

      { h2: 'Start from the question, not the feature list' },
      { p: 'A first version exists to answer the one thing that decides whether the business works. Usually it’s something like: will these people pay for this, or can we deliver this at a cost that leaves a margin?' },
      { p: 'Write that question down in a sentence. Then look at every proposed feature and ask whether it helps you answer it. Most won’t, and those can wait.' },

      { h2: 'Things that almost never belong in version one' },
      { ul: [
        'Your own login system. Use a hosted provider. Nobody has ever failed because their auth was rented.',
        'An admin dashboard. In the first months you have few enough records that you can manage them directly, and every hour spent on internal screens is an hour not spent on the product.',
        'Billing, if you can invoice people manually. Ten customers can be billed by hand. Build billing when the manual version starts hurting.',
        'Settings and preferences. Pick sensible defaults. Preferences are usually a way of avoiding a decision.',
        'Native mobile apps, when a good mobile web experience would answer the same question for a fraction of the cost.',
        'Scale work. You do not have traffic yet. Designing for a million users before you have ten is the most expensive form of optimism.',
      ] },

      { h2: 'Things founders cut that they shouldn’t' },
      { ul: [
        'Analytics. If you cannot see what people did, you learn nothing and the whole exercise was decorative.',
        'A way to talk to users. An email field and somewhere to reply is enough, but you need it.',
        'Basic security. Rented auth, no secrets in the repo, encrypted connections. Cheap now, ruinous later.',
        'Anything that makes the core thing feel good. If the one feature your business depends on feels clumsy, you will get a false negative and conclude the idea failed.',
      ] },

      { h2: 'The signs you have built too much' },
      { p: 'A few patterns show up again and again before launch:' },
      { ul: [
        'You are three months in and nobody outside the team has used it.',
        'You are building screens for situations that have never occurred.',
        'You can list features but not the question those features answer.',
        'The launch date has moved twice, each time for something that isn’t the core feature.',
      ] },

      { quote: 'If you aren’t slightly embarrassed by the first version, you probably spent too long on it.' },

      { h2: 'What we usually recommend' },
      { p: 'Start with a clickable prototype. It costs a fraction of a build, you can put it in front of ten people in a fortnight, and roughly half the time it changes what gets built. That is a cheap way to be wrong.' },
      { p: 'Then build the narrowest thing that answers the question, ship it to real users, and decide what is next from what they do rather than what they say. Keep the code clean enough to hand to an engineer you hire later, because if this works you will hire one.' },

      { h2: 'A note on raising money' },
      { p: 'If you plan to raise, someone technical will eventually look at what you own. They aren’t looking for elegance. They want to see that the code is understandable, that there are some tests, that deployment isn’t one person’s laptop, and that the accounts are in the company’s name. None of that requires building more. It requires building tidily.' },
    ],
  },
  {
    slug: 'what-a-website-costs-in-calgary',
    title: 'What a website costs in Calgary (and what drives the number)',
    metaTitle: 'What a Website Costs in Calgary | Codelithic Labs',
    metaDescription:
      'Real price ranges for websites in Calgary, what drives cost, and the five questions that change a quote more than anything else.',
    excerpt:
      'Quotes for the same brief often land between $3,000 and $80,000. Here’s what moves the number, and how to tell which end of the range you belong at.',
    category: 'Web Development',
    published: '2026-08-12',
    updated: '2026-08-12',
    readingMinutes: 8,
    tags: ['pricing', 'web development', 'calgary'],
    related: ['build-vs-buy', 'core-web-vitals-for-business-owners'],
    body: [
      { p: 'Ask five Calgary agencies to quote the same website and you’ll get five numbers that don’t appear to describe the same project. That’s not always padding. The brief usually leaves out the things that drive cost, so each shop fills the gaps with different assumptions.' },
      { p: 'Here’s the honest breakdown, including where we sit and when you shouldn’t hire us.' },

      { h2: 'The three realistic tiers' },
      { h3: 'Template site — roughly $3,000 to $10,000' },
      { p: 'A themed template configured with your content. Legitimate for a new business that needs credibility and a contact form. You’re buying speed, not fit. Expect to work within the template’s structure rather than your own.' },
      { p: 'Choose this if the site is a brochure and the business isn’t yet sure what it needs.' },

      { h3: 'Custom marketing site — roughly $15,000 to $45,000' },
      { p: 'Designed for your positioning, built to your content model, with performance and search handled on purpose. This is where most established businesses land. The cost is mostly design and content structure, not code.' },

      { h3: 'Web application — $50,000 and up' },
      { p: 'Accounts, dashboards, payments, integrations with systems you already run. The number scales with the number of things that can go wrong, which is a function of integrations far more than screens.' },

      { h2: 'What moves the number' },
      { ul: [
        'Content. If you need copywriting, photography and a migration of 200 existing pages, that’s often a third of the budget. If you hand over final copy, the quote drops immediately.',
        'Integrations. Each external system (CRM, ERP, booking, payments) adds scope and a permanent maintenance obligation. Two integrations isn’t twice one integration.',
        'Number of distinct page types, not number of pages. Forty pages in three templates is cheap. Eight pages in eight bespoke layouts isn’t.',
        'Approval structure. A single decision-maker is cheaper than a committee, because revision rounds are the least visible cost in any project.',
        'Compliance requirements. WCAG conformance, privacy obligations or sector-specific rules are real work and should be priced explicitly.',
      ] },

      { h2: 'The costs most quotes leave out' },
      { p: 'A website isn’t a capital purchase that sits still. Budget for the ongoing side before you commit to the build, because these are the line items that surprise people in year two:' },
      { ul: [
        'Hosting and domains — typically $20 to $200 a month depending on traffic and architecture.',
        'Dependency and security updates — an unmaintained site accumulates known vulnerabilities within months.',
        'Content changes — trivial if your CMS was set up for your team, expensive if every edit is a support ticket.',
        'Analytics and measurement — worth paying for, because without it you can’t tell whether any of the above was worth it.',
      ] },

      { h2: 'Five questions that change your quote' },
      { p: 'Answer these before you brief anyone, and the numbers you get back will be both lower and more comparable:' },
      { ul: [
        'Who writes the copy — you or us?',
        'What must this connect to that already exists?',
        'How many different page layouts do you need?',
        'Who signs off, and how many people must agree?',
        'What does success look like in numbers twelve months from now?',
      ] },

      { h2: 'When not to hire a custom shop' },
      { p: 'If you need a site live in two weeks, your budget is under $5,000, or you’re still testing whether the business works at all, use a good template and spend the difference on finding customers. We’ll tell you that on the call rather than after the invoice.' },

      { quote: 'The cheapest website is the one you don’t have to rebuild in eighteen months because it was never built for where the business was going.' },

      { h2: 'What to do next' },
      { p: 'Write down the answers to the five questions above and send them to two or three shops. Comparable inputs produce comparable quotes, and the differences that remain will tell you something real about each team.' },
    ],
  },

  {
    slug: 'build-vs-buy',
    title: 'Build or buy? A decision framework that survives contact with reality',
    metaTitle: 'Build vs Buy Software: A Practical Framework | Codelithic Labs',
    metaDescription:
      'When custom software is worth it and when off-the-shelf wins. A decision framework, the real cost of ownership, and the traps on both sides.',
    excerpt:
      'Custom software is usually the more expensive answer, and sometimes the only correct one. Here’s how to tell which situation you’re in before you commit.',
    category: 'Consulting',
    published: '2026-07-22',
    updated: '2026-07-22',
    readingMinutes: 7,
    tags: ['strategy', 'software development', 'consulting'],
    related: ['what-belongs-in-an-mvp', 'why-your-internal-tool-became-a-spreadsheet'],
    body: [
      { p: 'We’re a custom software shop, and most of the time we tell people to buy something. That’s not modesty, it’s arithmetic. A product with ten thousand customers has absorbed ten thousand customers’ worth of edge cases, and you get all of that for a subscription.' },
      { p: 'But sometimes buying is the expensive answer, and the framework below is how we tell the difference.' },

      { h2: 'Start with one question' },
      { quote: 'Is this process a source of competitive advantage, or is it overhead you wish you didn’t have to think about?' },
      { p: 'Buy the overhead. Payroll, accounting, email, CRM, helpdesk. These are solved problems and your version won’t be better. Build the advantage: the thing you do differently from competitors, that customers notice, that you wouldn’t want a competitor to be able to buy off a shelf.' },

      { h2: 'Four situations where building wins' },
      { ul: [
        'The gap is costing measurable money. If a product fits 60% of your workflow and the other 40% is three people re-keying data for ten hours a week, that’s roughly $40,000 a year in salary funding a permanent workaround.',
        'Integration is the actual product. You’ve six systems that don’t talk. Nobody sells "your six systems, connected." That layer is inherently custom.',
        'Vendor lock-in is a strategic risk. Your business depends on a tool whose pricing, roadmap or continued existence you don’t control.',
        'The process IS the differentiator. If how you do the work is why customers choose you, off-the-shelf software will actively erode that by pushing you toward how everyone else works.',
      ] },

      { h2: 'Four situations where buying wins' },
      { ul: [
        'The problem is common. Invoicing is invoicing. Your accounts receivable isn’t special, however much it feels that way.',
        'Compliance is part of the product. Payment processing and payroll tax carry regulatory burden that vendors absorb on your behalf. Taking that in-house is rarely worth it.',
        'You need it next month. Custom takes time. A product takes an afternoon.',
        'Nobody internally can own it. Custom software needs a decision-maker for its whole life, not just its build. Without that person it decays.',
      ] },

      { h2: 'The cost comparison people get wrong' },
      { p: 'Most build-vs-buy spreadsheets compare a one-off build cost against a monthly subscription, which flatters the build. The honest comparison includes:' },
      { ul: [
        'Build: development, plus hosting, plus 15–20% of build cost annually in maintenance, plus the internal time to specify and test it, plus the risk that it takes longer than estimated.',
        'Buy: subscription, plus per-seat growth as you hire, plus configuration and integration work, plus the cost of the workarounds where it doesn’t fit, plus switching cost if you ever leave.',
      ] },
      { p: 'Run both over five years, not one. The answer often reverses somewhere in year three, in whichever direction you didn’t expect.' },

      { h2: 'The middle path most people miss' },
      { p: 'Build-or-buy is rarely binary. The best answer is often to buy the commodity parts and build only the thin layer that’s yours: a custom interface over a bought engine, or an integration layer between three products you keep.' },
      { p: 'That approach keeps your maintenance surface small while still fitting how you work. It’s what we recommend more often than either extreme.' },

      { h2: 'A sanity check before you commit' },
      { p: 'Whichever way you lean, ask: if this decision is wrong, how much does it cost to reverse? Buying is usually cheaper to undo than building. When the two options look close, pick the reversible one.' },
    ],
  },

  {
    slug: 'core-web-vitals-for-business-owners',
    title: 'Core Web Vitals, explained for people who don’t write code',
    metaTitle: 'Core Web Vitals Explained for Business Owners | Codelithic Labs',
    metaDescription:
      'What Core Web Vitals measure, why Google cares, how much they affect rankings, and the fixes that deliver most of the benefit.',
    excerpt:
      'Three numbers decide whether Google thinks your site feels fast. Here’s what they mean in plain language, and which fixes are worth paying for.',
    category: 'Web Development',
    published: '2026-06-30',
    updated: '2026-09-02',
    readingMinutes: 6,
    tags: ['seo', 'performance', 'web development'],
    related: ['what-a-website-costs-in-calgary', 'seo-basics-for-calgary-businesses'],
    body: [
      { p: 'Your developer mentions Core Web Vitals, your rankings are flat, and you’re being asked to fund work you can’t see. This is what you’re buying.' },

      { h2: 'The three numbers' },
      { h3: 'Largest Contentful Paint (LCP) — should be under 2.5 seconds' },
      { p: 'How long until the biggest thing on screen shows up. Usually a hero image or headline. This is the closest thing to "does the page feel fast," and it’s the one most often broken by a single oversized image.' },

      { h3: 'Interaction to Next Paint (INP) — should be under 200 milliseconds' },
      { p: 'When someone taps a button, how long before the page visibly responds. This replaced First Input Delay in 2024 and is stricter, because it measures every interaction rather than just the first.' },

      { h3: 'Cumulative Layout Shift (CLS) — should be under 0.1' },
      { p: 'How much the page jumps around while loading. If you’ve ever gone to tap a link and hit an ad because the page moved, that’s CLS. It’s the cheapest of the three to fix and the most irritating to users.' },

      { h2: 'How much do they affect rankings?' },
      { p: 'Less than SEO vendors imply, and more than nothing. Google has been explicit that these are a tiebreaker: they won’t lift a weak page above a better one, but between two comparable results, the faster one wins.' },
      { p: 'The stronger argument is commercial rather than algorithmic. Slow pages lose people before they ever see your offer, and that shows up in conversion rate regardless of where you rank.' },

      { h2: 'The fixes that deliver most of the benefit' },
      { p: 'In our experience the order of impact per dollar is remarkably consistent:' },
      { ul: [
        'Compress and correctly size images. This alone fixes most LCP failures. A 5 MB hero image that should be 300 KB is the single most common problem we find.',
        'Set width and height on every image. This is a one-line fix per image and usually resolves most of a CLS problem.',
        'Remove or defer third-party scripts. Chat widgets, tag managers and tracking pixels are often the largest cost on the page, and each one is someone else’s code running on your site.',
        'Serve modern image formats. WebP or AVIF typically cut image weight by another 30% over optimised JPEG.',
        'Load fonts properly. Use font-display and preconnect so text is never invisible while a font downloads.',
      ] },
      { p: 'Notice that four of the five are about images and third-party code. Framework changes and rewrites, the expensive options, are rarely where the wins are.' },

      { h2: 'How to check your own site' },
      { p: 'Use PageSpeed Insights for a single page and Search Console’s Core Web Vitals report for the whole site. The distinction matters: PageSpeed runs a lab test on demand, while Search Console reports field data from real visitors. Field data is what Google uses, and it’s the one to trust when they disagree.' },

      { h2: 'A reasonable expectation' },
      { p: 'Most business sites can move from failing to passing in a few days of focused work, most of it on images. If someone quotes you a rebuild to fix Core Web Vitals, get a second opinion before you sign.' },
    ],
  },

  {
    slug: 'seo-basics-for-calgary-businesses',
    title: 'Local SEO for Calgary businesses: what moves the needle',
    metaTitle: 'Local SEO for Calgary Businesses | Codelithic Labs',
    metaDescription:
      'A practical local SEO guide for Calgary businesses: Google Business Profile, local search signals, reviews, and what to ignore.',
    excerpt:
      'For most Calgary businesses, one free Google product outperforms everything else combined, and it’s usually half-configured.',
    category: 'Digital Marketing',
    published: '2026-05-18',
    updated: '2026-08-05',
    readingMinutes: 7,
    tags: ['seo', 'local search', 'calgary', 'digital marketing'],
    related: ['core-web-vitals-for-business-owners', 'what-a-website-costs-in-calgary'],
    body: [
      { p: 'If you sell to people in Calgary, the highest-return marketing work available to you is usually free, takes an afternoon, and is sitting half-finished right now.' },

      { h2: 'Google Business Profile comes first' },
      { p: 'For local searches, the map pack sits above the normal results. Appearing there’s often worth more than ranking first organically, and it’s determined largely by a profile you control directly.' },
      { p: 'Work through all of it, not just the three fields that are quickest:' },
      { ul: [
        'Exact, consistent business name, address and phone number. Consistency across the web matters more than the formatting you choose.',
        'Correct primary category. This is one of the strongest signals in the whole system and is often set wrong.',
        'Service areas, if you travel to clients.',
        'Real photographs, updated periodically. Profiles with current photos get more engagement, and engagement feeds ranking.',
        'Products and services listed individually rather than as one paragraph.',
        'Hours, including holiday hours. Wrong hours generate angry reviews.',
      ] },

      { h2: 'Reviews are ranking signals and sales assets' },
      { p: 'Volume, recency and your responses all matter. A steady trickle beats forty reviews collected in one week, which looks exactly as artificial as it’s.' },
      { p: 'Ask every satisfied customer, make it one tap, and respond to everything, especially the negative ones, calmly and factually. Prospects read your response to criticism far more carefully than they read the praise.' },
      { p: 'Never buy reviews. It violates Google’s policies, it’s increasingly easy to detect, and the downside is losing the profile that drives your leads.' },

      { h2: 'What your website needs to contribute' },
      { ul: [
        'Your name, address and phone number in the footer as real text, matching your profile exactly.',
        'LocalBusiness structured data so search engines can read your details unambiguously.',
        'A genuine page per service, rather than one page listing eight services in bullet form.',
        'Content that mentions Calgary naturally, because you work here, not stuffed into every heading.',
      ] },

      { h2: 'What to ignore' },
      { ul: [
        'Directory submission packages. A handful of quality citations matter; four hundred low-grade directory listings do nothing.',
        'Doorway pages for surrounding towns. A thin page per suburb is a pattern Google specifically targets. One strong page beats twelve thin ones.',
        'Keyword density. It hasn’t worked for over a decade.',
        'Anyone guaranteeing a number-one ranking. Nobody can guarantee that, and the guarantee itself tells you what you need to know.',
      ] },

      { h2: 'A realistic timeline' },
      { p: 'Google Business Profile improvements can show up within two to four weeks. Website and content work compounds over three to six months. Anyone promising faster is either taking credit for something that was already happening or doing something you’ll regret.' },

      { h2: 'The order we would do it in' },
      { p: 'Fix the profile. Build a review habit. Make sure the site loads quickly and says where you’re. Then, and only then, start producing content. Most businesses do that list backwards and wonder why the blog isn’t working.' },
    ],
  },

  {
    slug: 'why-your-internal-tool-became-a-spreadsheet',
    title: 'Why your most important business process is running on a spreadsheet',
    metaTitle: 'When to Replace a Business-Critical Spreadsheet | Codelithic Labs',
    metaDescription:
      'Spreadsheets quietly become critical infrastructure. How to tell when yours has outgrown itself, and what to do about it without a risky rewrite.',
    excerpt:
      'Nobody decided the business would run on a spreadsheet. It just kept working, until the day it didn’t. Here’s how to tell where yours is on that curve.',
    category: 'Software Development',
    published: '2026-04-09',
    updated: '2026-04-09',
    readingMinutes: 6,
    tags: ['internal tools', 'software development', 'operations'],
    related: ['build-vs-buy', 'what-a-website-costs-in-calgary'],
    body: [
      { p: 'Every established business has one. A workbook with a name like "MASTER_final_v4_USE_THIS.xlsx", understood in full by exactly one person, that the business can’t operate without.' },
      { p: 'It got there honestly. Spreadsheets are the fastest way to model a process, and that flexibility is a real strength. The problem is that nothing ever signals when it’s time to graduate.' },

      { h2: 'Signals it has outgrown itself' },
      { ul: [
        'More than one person needs it at once, and your process for that’s asking in Slack whether anyone has it open.',
        'It contains data you would have to disclose if it leaked: personal information, payroll, customer records.',
        'Someone re-keys its contents into another system. That’s a synchronisation job being done by a human.',
        'There’s a recovery story that begins "we restore last week’s copy and redo the week."',
        'Only one person fully understands the formulas, and the business has quietly become dependent on them not leaving.',
        'Errors have reached customers. Once is bad luck; twice is a structural problem.',
      ] },
      { p: 'One of these is fine. Three or more and you’re carrying a risk you have not priced.' },

      { h2: 'What it’s costing' },
      { p: 'Spreadsheet risk hides because it’s diffuse. Add up the hours spent re-keying, the time lost reconciling versions, the cost of errors that reached a customer, and the value of decisions delayed because nobody trusted the numbers. In most businesses we look at, the annual figure comfortably exceeds the cost of replacing it.' },

      { h2: 'Replace it without the risky rewrite' },
      { p: 'The instinct is a full custom system. Resist it. Big-bang replacements of processes nobody has fully documented fail at a high rate, because the spreadsheet encodes years of undocumented exceptions.' },
      { p: 'A lower-risk sequence:' },
      { ul: [
        'Document what it does, including the exceptions people handle manually. This alone is often revealing.',
        'Move the data into a real database first, leaving the process alone. Concurrency, history and backup problems disappear immediately.',
        'Replace the single highest-pain interaction with a proper interface. Usually data entry.',
        'Add validation, so bad data can’t get in rather than being cleaned up later.',
        'Only then automate the parts that are repetitive.',
      ] },
      { p: 'Each step is independently useful. If budget disappears after step two, you’re still better off than you started, which isn’t true of a rewrite abandoned halfway.' },

      { h2: 'When to leave it alone' },
      { p: 'Not every spreadsheet needs replacing. If one person uses it, the data isn’t sensitive, and an error would be an inconvenience rather than an incident, it’s doing its job. Flexibility is a genuine feature and software will take some of it away.' },
      { quote: 'The goal isn’t to eliminate spreadsheets. It’s to stop betting the business on one.' },
    ],
  },
];
